import ListInfo from '@/parcours/flows/shared/listInfo.vue';
import { usePreventionParcours } from '@/parcours/composables/usePreventionParcours';
import defaultValidators from '@/helpers/default-validators';
import { withGlobalFlow } from '@/parcours/flowGlobalSteps';
import TextInput from '@/molecules/TextInput.vue';
import RadioRowItem from '@/molecules/RadioRowItem.vue';
import { updatePatient } from '@/services/patients';
import { getProSync } from '@/services/auth';
import { useMission } from '@/composables/useMissions';
import { getBirthdayLabel } from '@/helpers/patient';

const { update, getById: getByIdParcours } = usePreventionParcours(
  '/cms/coordinator-patient-flu-infos',
);
const { getById: getMissionById } = useMission();
export const fluFlow = withGlobalFlow({
  id: 'fluFlow',

  basePath: '/fluFlow/flow',

  exitRoute: '/fluFlow',
  async onInit({ route }) {
    const reservationId = route.params.reservationId;
    const missionId = route.params.missionId;
    const slotId = route.params.slotId;
    const [data, mission] = await Promise.all([
      getByIdParcours(reservationId),
      getMissionById(missionId),
    ]);

    return {
      ...data,
      mission,
      _missionId: missionId,
      _slotId: slotId,
      _reservationId: reservationId,
    };
  },

  steps: {
    isEligibleVaccination: {
      type: 'form',
      title: 'Eligiblité',
      description: 'Le patient est-il éligible à la vaccination ?',
      schema: {
        validateOnChange: false,
        validateOnBlur: true,
        fields: [
          {
            id: 'eligible',
            type: 'radio-group',
            as: RadioRowItem,
            options: [
              {
                label: 'Oui',
                value: true,
              },
              {
                label: 'Non',
                value: false,
              },
            ],
            optionValue: 'value',
            optionLabel: (option) => option.label,
            rules: defaultValidators.radioGroup,
          },
        ],
      },
    },
    fluInfo: {
      type: 'component',
      component: ListInfo,
      title: 'Vaccination antigrippale',
      props: (data: any) => ({
        data,
        config: [
          { key: 'mission.vaccin_name', label: 'Type de vaccin', source: 'flowData' },
          { label: "Mode d'administration", value: 'Intra-musculaire (IM)' },
        ],
      }),
    },
    infoVaccination: {
      type: 'form',
      title: 'Information vaccin',
      schema: {
        validateOnChange: false,
        validateOnBlur: true,
        fields: [
          {
            id: 'lot_number',
            type: 'text',
            as: TextInput,
            label: 'N° de Lot',
            rules: defaultValidators.text,
          },
          {
            id: 'vaccin_expiration_date',
            type: 'datetime-local',
            as: TextInput,
            label: 'Date de péremption du vaccin',
            rules: defaultValidators.date,
          },
          {
            id: 'injection_area',
            type: 'radio-group',
            as: RadioRowItem,
            label: "Zone d'injection",
            options: [
              {
                label: 'Bras droit',
                value: 'bras droit',
              },
              {
                label: 'Bras gauche',
                value: 'bras gauche',
              },
              {
                label: 'Autre',
                value: 'autre',
              },
            ],
            optionValue: 'value',
            optionLabel: (option) => option.label,
            rules: defaultValidators.radioGroup,
          },
        ],
      },
    },
    attestationVaccination: {
      type: 'component',
      component: ListInfo,
      title: 'Attestation de vaccination',
      props: (data: any) => {
        const pro = getProSync();
        const patient = data?.flowData?.patient;
        const mission = data?.flowData?.mission;
        const lot = data?.flowData?.lot_number;
        const now = new Date();
        const soussigne = pro?.civility === 'F' ? 'soussignée' : 'soussigné';
        const numType = pro?.rpps ? 'n° RPPS :' : 'n° ADELI :';
        const numValue = pro?.rpps || pro?.adeli;
        const nee = patient?.civility === 'F' ? 'née' : 'né';
        const birthStr = getBirthdayLabel(patient) || '-';
        const dateStr = now.toLocaleDateString('fr-FR');
        const timeStr = now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

        return {
          data,
          config: [
            {
              section: 'Informations sur le patient',
              items: [
                { key: 'patient.lastname', label: 'Nom', source: 'flowData' },
                { key: 'patient.firstname', label: 'Prénom', source: 'flowData' },
                { label: 'Date de naissance', value: getBirthdayLabel(patient) || '-' },
              ],
            },
            {
              section: 'Informations sur le vaccin',
              items: [
                { key: 'mission.vaccin_name', label: 'Type de vaccin', source: 'flowData' },
                { key: 'lot_number', label: 'Numéro de lot', source: 'flowData' },
                {
                  key: 'vaccin_expiration_date',
                  label: 'Date de péremption du vaccin',
                  source: 'flowData',
                  type: 'date',
                },
                {
                  key: 'vaccination_date_display',
                  label: "Date d'administration du vaccin",
                  value: dateStr,
                },
                {
                  key: 'vaccination_time_display',
                  label: "Heure d'administration du vaccin",
                  value: timeStr,
                },
              ],
            },
            {
              section: "Informations sur l'infirmier",
              items: [
                { key: 'pro_lastname', label: 'Nom', value: pro?.lastname },
                { key: 'pro_firstname', label: 'Prénom', value: pro?.firstname },
                {
                  key: 'pro_number',
                  label: pro?.rpps ? 'Numéro RPPS' : 'Numéro ADELI',
                  value: pro?.rpps || pro?.adeli,
                },
              ],
            },
            {
              checkbox: true,
              label: `Je ${soussigne} ${pro?.lastname} ${pro?.firstname} (${numType} ${numValue}) certifie avoir vacciné ${patient?.firstname} ${patient?.lastname}, ${nee} le ${birthStr} avec le vaccin ${mission?.vaccin_name} (n° de lot : ${lot}), le ${dateStr} à ${timeStr}`,
              errorMessage:
                "Vous devez certifier avoir vacciné ce patient avant d'envoyer l'attestation",
            },
          ],
        };
      },
    },
  },

  transitions: {
    isEligibleVaccination: {
      next: ({ data }) => {
        if (data.eligible === 'true' || data.eligible === true) {
          return 'fluInfo';
        }
        return {
          customSubmit: true,
          action: () => updatePatient(data?.patient?.id, { eligible: false }),
          redirect: `/missions/${data._missionId}/slots/${data._slotId}`,
        };
      },
    },
    fluInfo: {
      next: 'infoVaccination',
    },
    infoVaccination: {
      next: 'attestationVaccination',
    },
    attestationVaccination: {
      submit: true,
    },
  },

  async onSubmit(data) {
    const vaccinationDate = new Date().toISOString().replace('T', ' ').slice(0, 19);

    await update(data?.id, {
      vaccin_name: data?.mission?.vaccin_name,
      ...data,
      vaccination_date: vaccinationDate,
    });
  },
});
